'use client';

import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { ButtonIcon } from '../ui/button-icon';

export type BottomBarProps = {
  className?: {
    root?: string;
  };
  items?: BottomBarItemProps[];
};

export function BottomBar({ items = HEADER_CONFIG.bottomBar.items, ...props }: BottomBarProps) {
  return (
    <div
      className={cn(
        // `md:hidden`, matching the mobile header row: with `sm:hidden` nothing offered cart,
        // profile or favourites between 640 and 767px (the desktop header starts at `md`).
        'fixed bottom-0 left-0 right-0 top-auto z-50 grid h-[57px] grid-cols-5 gap-0 bg-background px-4 pb-0 pt-2 md:hidden',
        'ea-bottom-bar',
        props.className?.root,
      )}
    >
      {items?.map(({ key, ...item }) => (
        <BottomBarItem key={key} id={key} {...item} />
      ))}
    </div>
  );
}

export type BottomBarItemProps = {
  key: string;
  id?: string;
  activeIcon?: string;
  inactiveIcon?: string;
  label?: string;
  className?: {
    root?: string;
    iconContainer?: string;
    label?: string;
    active?: string;
  };
  href: string | (() => string);
};
export function BottomBarItem(props: BottomBarItemProps) {
  if (!props.activeIcon && !props.inactiveIcon && !props.label) {
    throw new Error('Either activeIcon, inactiveIcon or label must be provided');
  }
  const pathname = usePathname();
  let href = typeof props.href === 'function' ? props.href() : props.href;
  const isActive = arePathsEqual(pathname, href);
  const me = useCustomerStore(s => s.me);
  const isCustomerStoreInitialized = useCustomerStore(s => s.isInitialized);

  if (
    !me &&
    isCustomerStoreInitialized &&
    (href === ROUTES.PROFILE() || href === ROUTES.FAVORITES())
  ) {
    href = ROUTES.SIGN_IN();
  }

  const { t } = useTranslations();

  return (
    <Link
      href={href ?? '#'}
      className={cn(
        'flex h-[45px] flex-col items-center gap-1 rounded-full pb-0.5 pt-1',
        'ea-bottom-bar__item black',
        props.className?.root,
        {
          'ea-bottom-bar__item--active bg-[#FFFACC] text-primary': isActive,
          // The 11px home label needs more contrast on its pale yellow active background.
          'text-[#0062ED]': isActive && props.id === 'home' && pathname === ROUTES.HOME(),
        },
      )}
      prefetch={false}
    >
      <ButtonIcon
        alt={props.id ?? ''}
        url={
          isActive
            ? (props.activeIcon ?? '/images/placeholder.png')
            : (props.inactiveIcon ?? '/images/placeholder.png')
        }
        className="max-h-6 min-h-6 min-w-6 max-w-6"
        prioritize
      />
      {props.label && (
        <div
          className={cn(
            'text-center text-[11px] leading-[11px]',
            'ea-bottom-bar__item__label',
            props.className?.label,
          )}
        >
          {t(props.label)}
        </div>
      )}
    </Link>
  );
}
